<template>
  <v-container class="helmi-search-watch-management">
    <v-row no-gutters>
      <v-col>
        <v-card>
          <v-toolbar flat>
            <v-icon class="mr-3">
              pageview
            </v-icon>
            <v-toolbar-title class="text-h5 text-md-h4">
              Hakuvahdin hallinta
            </v-toolbar-title>
          </v-toolbar>
          <v-window v-model="step">
            <v-window-item :value="1">
              <v-fade-transition mode="out-in">
                <v-list v-if="initializing">
                  <v-list-item>
                    <v-list-item-content class="d-flex align-center">
                      <v-skeleton-loader
                        type="chip, list-item-two-line, actions"
                      />
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <div v-else>
                  <v-card-text class="pa-1">
                    <helmi-search-watch-active-filters
                      v-if="active"
                      :filters="watch"
                      class="mb-1 mx-1"
                      color="primary"
                      outlined
                      small
                    />

                    <v-list>
                      <v-list-item v-if="active">
                        <v-list-item-content>
                          <v-list-item-title>Voimassaoloaika</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ watch.created | date }} - {{ watch.ends | date }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-menu bottom left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon>more_vert</v-icon>
                              </v-btn>
                            </template>

                            <v-list>
                              <v-list-item
                                v-for="(item, i) in [1, 3, 6]"
                                :key="i"
                                @click="onExtend(item)"
                              >
                                <v-list-item-title>
                                  Pidennä voimassaoloaikaa {{ item }}kk
                                </v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-list-item-action>
                      </v-list-item>
                      <v-list-item v-else>
                        <v-list-item-content>
                          <v-list-item-title>Hakuvahti poistettu käytöstä</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ watch.updatedAt | date({ type: 'full' }) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                  <v-card-actions v-if="active" class="pa-4">
                    <v-spacer/>
                    <v-btn
                      text
                      @click="removalDialog = true"
                    >
                      Poista hakuvahti
                    </v-btn>
                    <v-btn
                      v-if="active"
                      color="primary"
                      @click="step = 2"
                    >
                      Muokkaa
                    </v-btn>
                  </v-card-actions>
                </div>
              </v-fade-transition>
            </v-window-item>
            <v-window-item :value="2">
              <v-card-text>
                <v-form ref="searchForm">
                  <helmi-search-watch-fields
                    :items="watch"
                    :numeric-fields="['property_type']"
                  />

                  <v-card-actions style="width: 100%">
                    <v-spacer/>
                    <v-btn
                      text
                      @click="step = 1"
                    >
                      Peruuta
                    </v-btn>
                    <v-btn
                      :disabled="saving"
                      :loading="saving"
                      color="primary"
                      @click="onSave"
                    >
                      Tallenna muutokset
                    </v-btn>
                  </v-card-actions>
                </v-form>
              </v-card-text>
            </v-window-item>
          </v-window>
        </v-card>
      </v-col>
    </v-row>

    <v-overlay
      color="white"
      opacity="0.85"
      :value="overlay.active"
    >
      <v-card
        color="transparent"
        flat
        light
      >
        <v-row class="text-center">
          <v-col cols="12">
            <v-avatar
              :color="overlay.color"
              size="98"
            >
              <v-icon color="white" size="64">
                {{ overlay.icon }}
              </v-icon>
            </v-avatar>
          </v-col>
          <v-col cols="12" class="">
            <p class="text-h4">
              {{ overlay.text }}
            </p>
          </v-col>
        </v-row>
      </v-card>
    </v-overlay>

    <v-dialog
      v-model="removalDialog"
      max-width="290"
    >
      <v-card>
        <v-card-title class="headline">
          Poistetaanko hakuvahti?
        </v-card-title>

        <v-card-text>
          Hakuvahtisi poistetaan pysyvästi.
        </v-card-text>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            text
            @click="removalDialog = false"
          >
            Peruuta
          </v-btn>

          <v-btn
            :disabled="deActivating"
            :loading="deActivating"
            text
            @click="onDeactivate()"
          >
            Poista
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { isAnObject } from '@/utils';

export default {
  name: 'HelmiSearchWatchManagement',

  components: {
    'helmi-search-watch-fields': () => import('@/components/searchWatch/SearchWatchFields'),
    'helmi-search-watch-active-filters': () => import('@/components/searchWatch/searchWatchActiveFilters'),
  },

  props: {
    id: {
      type: Number,
      default: null,
    },
  },

  data: () => ({
    extending: false,
    deActivating: false,
    initializing: false,
    overlay: {
      active: false,
      text: '',
      icon: 'check',
    },
    removalDialog: false,
    saving: false,
    step: 1,
  }),

  computed: {
    ...mapState({
      watchCount: state => state.searchWatch.allIds.length,
    }),
    ...mapGetters({
      getWatchById: 'searchWatch/find',
    }),

    active() {
      return +this.watch.active === 1;
    },

    watch() {
      return this.getWatchById(this.$route.params.watchId);
    },
  },

  async mounted() {
    if (this.watchCount === 0) {
      this.initializing = true;
      await this.getSearchWatch({ id: this.$route.params.watchId });
      this.initializing = false;
    }
  },

  methods: {
    ...mapActions({
      updateSearchWatch: 'searchWatch/update',
      getSearchWatch: 'searchWatch/getOne',
    }),

    displayOverlay({ color, icon, text }) {
      this.overlay = {
        active: true,
        icon,
        text,
        color,
      };

      setTimeout(() => {
        this.overlay = {
          active: false,
          icon: '',
          text: '',
          color: '',
        };
      }, 3000);
    },

    async onDeactivate() {
      this.deActivating = true;
      await this.updateSearchWatch({
        data: {
          id: this.watch.id,
          active: 0,
        },
      });

      this.displayOverlay({
        icon: 'close',
        text: 'Hakuvahti poistettu',
        color: 'error',
      });

      this.removalDialog = false;
      this.deActivating = false;
    },

    async onExtend(months) {
      this.extending = true;

      const date = new Date(this.watch.ends);

      await this.updateSearchWatch({
        data: {
          id: this.watch.id,
          ends: new Date(date.setMonth(date.getMonth() + months))
            .toISOString().slice(0, 19).replace('T', ' '),
        },
      });

      this.displayOverlay({
        icon: 'update',
        text: 'Hakuvahti päivitetty',
        color: 'primary',
      });

      this.extending = false;
    },

    async onSave() {
      this.saving = true;

      const watch = Object.entries(this.watch)
        .reduce((items, [key, item]) => {
          if (isAnObject(item)) {
            // eslint-disable-next-line no-param-reassign
            items = { ...items, ...item };
            return items;
          }
          // eslint-disable-next-line no-param-reassign
          items[key] = item;
          return items;
        }, {});

      watch.property_types = [...new Set(watch.property_types.map(t => parseInt(t, 10)))];

      await this.updateSearchWatch({ data: watch, mutate: false });
      this.displayOverlay({
        icon: 'check',
        text: 'Hakuvahti päivitetty',
        color: 'primary',
      });
      this.saving = false;
    },
  },
};
</script>

<style lang="scss">
.helmi-search-watch-management {

}
</style>
